import ParameterValidator from '@mvps-genesis/parameter-validator';

/*
 *--------------------------------------------------------------------------
 * AjaxVolumePricing
 *--------------------------------------------------------------------------
 *
 * Use this class to asynchronously update volume pricing content.
 *
 */
export default class AjaxVolumePricing {

	/**
	 * Parameter Validator class object
	 *
	 * @var	{ParameterValidator} @mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * Cached volume pricing data object. Used for limiting the number of requests to the api url for collecting the volume pricing data. The object keys are the variant ids and each key value contains an object with the volume pricing table entries and HTML template.
	 *
	 * @var {Object}
	 */
	#variantDataCache;

	/**
	 * The volume pricing root container element
	 *
	 * @var {HTMLElement}
	 */
	containerElement;

	/**
	 * The API page url; this will typically be the JAPI page
	 *
	 * @var {String}
	 */
	apiUrl;

	/**
	 * Create a new AjaxVolumePricing object.
	 *
	 * @param  {HTMLElement|String}  containerElement  The volume pricing root container element.
	 * @param  {String}              options.apiUrl    The api url. Will default to window.mivaJS.Japi_Url if an empty value is provided. Defaults to ''.
	 */
	constructor (containerElement, {
		apiUrl = ''
	} = {}) {
		this.#variantDataCache = {};
		this.#validator = new ParameterValidator(this);

		this.containerElement = this.#validator.isElement('containerElement', containerElement);

		this.apiUrl = this.#validator.isString('apiUrl', apiUrl || (window.mivaJS ? window.mivaJS.Japi_Url : ''));

		this.#validator.isUrl('apiUrl', this.apiUrl);
	}

	/* eslint-disable camelcase */
	/**
	 * Update the volume pricing container element's content
	 *
	 * @async
	 * @param  {Object} productData Object containing the product code and variant id. This should be or resemble the object returned to the 'variant_changed' Miva Event callback function.
	 */
	async updateContent (productData) {
		const productCode = this.#validator.isDefined('productData.product_code', productData.product_code);
		const variantId = productData.variant_id ? parseInt(productData.variant_id, 10) : 0;
		let volumePricingTemplate = '';

		if (this.#variantDataCache[variantId]) {
			volumePricingTemplate = this.#variantDataCache[variantId].template;
		} else {
			const volumePricingData = await this.getVolumePricingData(productCode, variantId);

			if (volumePricingData && volumePricingData.success && volumePricingData.data) {
				if (volumePricingData.data.template) {
					volumePricingTemplate = volumePricingData.data.template;
				}

				this.#variantDataCache[variantId] = {
					discount_volume: volumePricingData.data.discount_volume ?? {},
					template: volumePricingTemplate
				};
			}
		}

		this.containerElement.innerHTML = volumePricingTemplate;
	}
	/* eslint-enable camelcase */

	/**
	 * Retrieve the volume pricing data
	 *
	 * @async
	 * @param  {String} productCode The product code.
	 * @param  {Int}    variantId   The variant id.
	 * @return {Object}             The HTTP response object for the volume pricing data request.
	 */
	async getVolumePricingData (productCode, variantId = 0) {
		this.#validator.isString('getVolumePricingData.productCode', productCode);

		if (productCode === '') {
			throw new TypeError(`${this.constructor.name} :: Invalid product code provided`);
		}

		this.#validator.isInt('getVolumePricingData.variantId', variantId);

		/* eslint-disable camelcase */
		const response = await fetch(this.apiUrl, {
			body: JSON.stringify({
				function: 'load-volume-pricing',
				product_code: productCode,
				variant_id: variantId
			}),
			headers: {
				'Content-Type': 'application/json'
			},
			method: 'POST'
		});
		/* eslint-enable camelcase */

		if (!response.ok) {
			throw Error(response.statusText);
		}

		return response.json();
	}

	/**
	 * Retrieve the variant data cache object.
	 *
	 * @return {Object} The variant data cache object.
	 */
	getVariantDataCache () {
		return this.#variantDataCache;
	}
}
